import { FC, ReactNode, useMemo } from 'react'
import styles from 'components/features/pfm/SegmentButton/SegmentButton.styl'
import cn from 'classnames'
import { getChildrenWithType } from 'tangle-shared'
import ReactSvgIcon from 'icons/*.svg'
import SvgIcon from 'components/atoms/SvgIcon'
import { AnalyticsClick } from 'components/features/pfm/AnalyticsClick/AnalyticsClick'
import { AnalyticsEvent } from 'interfaces/analytics'
import { shortenNumber } from 'helpers/formatHelpers'

type SegmentProps = {
  className?: string
  kind?: 'counter'
}

const Segment: FC<SegmentProps> = (props) => {
  const { kind, className } = props

  const rootClassName = cn(
    styles.segment,
    kind && styles.counterSegment,
    className
  )

  return (
    <span className={ rootClassName }>
      { props.children }
    </span>
  )
}

type IconProps = {
  size: number
  src: typeof ReactSvgIcon | null
}
const Icon: FC<IconProps> = (props) => {
  const { size, src } = props

  return (
    <span className={ styles.icon } style={{ width: size, height: size }}>
      <SvgIcon src={ src } />
    </span>
  )
}

type TextProps = {
  text: string
}

const Text: FC<TextProps> = (props) => {
  return (
    <span className={ styles.text }>
      { props.text }
    </span>
  )
}

type SegmentButtonProps = {
  disabled?: boolean
  counter?: number
  pressed: boolean
  analyticsAction: AnalyticsEvent | null
  onClick?: () => void
}

export const SegmentButton: FC<SegmentButtonProps> & {
  Segment: typeof Segment
  Icon: typeof Icon
  Text: typeof Text
} = (props) => {
  const { children, counter, disabled, pressed, analyticsAction, onClick } = props

  const segments = getChildrenWithType(children, Segment)

  const segmentChildren: ReactNode[] = []
  if (!segments || !segments.length) {
    segmentChildren.push(children)
  } else if (segments.length === 1) {
    segmentChildren.push(segments[0])
  } else {
    segmentChildren.push(...segments)
  }

  if (typeof counter === 'number') {
    const counterSegment = typeof counter === 'number' && (
      <SegmentButton.Segment key='counter' kind='counter'>
        <SegmentButton.Text text={ shortenNumber(counter) } />
      </SegmentButton.Segment>
    )

    segmentChildren.push(counterSegment)
  }

  const rootClassName = cn(styles.segmentButton, {
    [styles.pressed]: pressed,
  })

  const Wrapper = useMemo<FC>(() => {
    if (analyticsAction) {
      return ({ children: wrapperChildren }) => (
        <AnalyticsClick { ...analyticsAction }>
          { wrapperChildren }
        </AnalyticsClick>
      )
    }

    return ({ children: wrapperChildren }) => (
      <>
        { wrapperChildren }
      </>
    )

  }, [analyticsAction])

  return (
    <Wrapper>
      <button
        className={ rootClassName }
        disabled={ disabled }
        data-pressed={ pressed }
        onClick={ onClick }
      >
        { segmentChildren }
      </button>
    </Wrapper>
  )
}

SegmentButton.Segment = Segment
SegmentButton.Icon = Icon
SegmentButton.Text = Text

